"use client";

import { startTransition, useEffect, useMemo } from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@tracify/ui/select";
import { chatModels, isSelectedChatModel } from "@tracify/shared";
import { cn } from "@/lib/utils";
import { chatModalModelAtom } from "@/atoms";
import { useAtom } from "jotai";

function formatTokenCount(tokens: number) {
  if (tokens >= 1_000_000) {
    return `${tokens / 1_000_000}M`;
  }

  if (tokens >= 1_000) {
    return `${tokens / 1_000}K`;
  }

  return tokens.toLocaleString("en-US");
}

export function ModelSelector({ className }: { className?: string }) {
  const [chatModel, setChatModel] = useAtom(chatModalModelAtom);

  const selectedChatModel = useMemo(
    () => chatModels.find((model) => model.id === chatModel),
    [chatModel]
  );

  useEffect(() => {
    if (!selectedChatModel) {
      setChatModel(chatModels[0].id);
    }
  }, [selectedChatModel, setChatModel]);

  return (
    <Select
      value={chatModel}
      onValueChange={(val) => {
        if (!isSelectedChatModel(val)) {
          return;
        }

        startTransition(() => {
          setChatModel(val);
        });
      }}
    >
      <SelectTrigger
        id="model-selector"
        data-testid="model-selector"
        className={cn("w-fit border-none hover:bg-muted", className)}
        size="sm"
        showIcon={false}
      >
        {selectedChatModel?.name ?? chatModels[0].name}
      </SelectTrigger>
      <SelectContent className="min-w-[300px]">
        <SelectGroup className="space-y-1">
          {chatModels.map((model) => {
            const { id } = model;

            return (
              <SelectItem
                key={id}
                value={id}
                data-testid={`model-selector-item-${id}`}
              >
                <div className="text-sm font-medium leading-tight">
                  {model.name}
                </div>
                <div className="mt-1 text-xs font-normal leading-none text-muted-foreground">
                  {model.description}
                </div>
                <div className="mt-1 text-xs font-normal leading-none text-muted-foreground">
                  {formatTokenCount(model.contextWindowTokens)} context /{" "}
                  {formatTokenCount(model.maxOutputTokens)} output
                </div>
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
